import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseTract } from 'src/models/dlcLease/ILeaseTract';
import { calculateLeaseTractBonusSummary, calculateLeaseTractTotals, leaseBonusPerAcreDiffers } from './leaseTractCalculations';
import * as styles from './styles';

interface ITractSectionProps {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: ILeaseTract[]) => void;
    onRefresh?: () => Promise<void>;
}

const TractSection: React.FC<ITractSectionProps> = ({ leaseMaster, onChange, onRefresh }) => {
    const isEditable = false;
    const { get, response, loading } = useFetch();
    const [expandedTractIndex, setExpandedTractIndex] = useState<number | null>(null);
    const [selectedTractIndex, setSelectedTractIndex] = useState<number | null>(0);
    const [legalModalIndex, setLegalModalIndex] = useState<number | null>(null);
    const [legalDescription, setLegalDescription] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState('');
    const tracts: ILeaseTract[] = (leaseMaster.tracts || []).filter((t) => t.operation !== 'delete');
    const firstTractKey = tracts[0]?.ltTg || tracts[0]?.trId || null;
    const selectedTract = selectedTractIndex !== null ? tracts[selectedTractIndex] || tracts[0] : tracts[0];

    useEffect(() => {
        setSelectedTractIndex(tracts.length > 0 ? 0 : null);
    }, [leaseMaster.lsNo, firstTractKey, tracts.length]);

    useEffect(() => {
        if (selectedTractIndex === null) {
            return;
        }

        if (selectedTractIndex >= tracts.length) {
            setSelectedTractIndex(tracts.length - 1);
        }
    }, [selectedTractIndex, tracts.length]);

    const totals = calculateLeaseTractTotals(leaseMaster.tracts);
    const tractBonusSummary = calculateLeaseTractBonusSummary(leaseMaster.tracts);
    const bonusPerAcreDiffers = leaseBonusPerAcreDiffers(tractBonusSummary.bonusPerAcre, leaseMaster.bonusAcre);

    const formatCurrency = (amount?: number): string => {
        if (amount === undefined || amount === null) {
            return '-';
        }

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    const formatNumber = (num?: number): string => {
        if (num === undefined || num === null) {
            return '-';
        }

        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 7,
            maximumFractionDigits: 7,
        }).format(num);
    };

    const formatTractNumber = (tract?: ILeaseTract): string => {
        if (!tract?.trNo) {
            return '-';
        }

        return `${tract.trNo}${tract.trCno ? `-${tract.trCno}` : ''}`;
    };

    const handleTractChange = (index: number, field: keyof ILeaseTract, value: string | number | null) => {
        if (!onChange) {
            return;
        }

        const updatedTracts = [...tracts];
        updatedTracts[index] = {
            ...updatedTracts[index],
            [field]: value,
        };
        onChange('tracts', updatedTracts);
    };

    const handleAddTract = () => {
        if (!onChange) {
            return;
        }

        const newTract: ILeaseTract = {
            lsNo: leaseMaster.lsNo,
            clNo: null,
            prNo: null,
            scNo: null,
            trNo: null,
            trCno: null,
            hzNo: null,
            net: null,
            gross: null,
            interest: null,
            bonus: null,
            rental: null,
            recFee: null,
            bank1: null,
            bank2: null,
            other: null,
            fees: null,
            mapLocation: '',
            ownershipType: '',
            client: '',
            prospect: '',
            section: '',
            tractDescription: '',
            horizonDescription: '',
            mapLegalDescription: '',
            operation: 'add',
        };

        const updatedTracts = [...tracts, newTract];
        onChange('tracts', updatedTracts);
        setExpandedTractIndex(updatedTracts.length - 1);
    };

    const handleRemoveTract = (index: number) => {
        if (!onChange || !window.confirm('Are you sure you want to remove this tract?')) {
            return;
        }

        const updatedTracts = [...tracts];
        updatedTracts[index] = {
            ...updatedTracts[index],
            operation: 'delete',
        };
        onChange('tracts', updatedTracts);
        setExpandedTractIndex(null);
    };

    const handleNumberFieldChange = (index: number, field: keyof ILeaseTract, value: string) => {
        const numValue = value === '' ? null : parseFloat(value);
        handleTractChange(index, field, numValue);
    };

    const toggleTractExpansion = (index: number) => {
        const newIndex = expandedTractIndex === index ? null : index;
        setExpandedTractIndex(newIndex);
        if (newIndex !== null) {
            tracts[newIndex].operation = 'update';
        }
    };

    const refreshTracts = async () => {
        if (!onRefresh || isRefreshing) {
            return;
        }

        setIsRefreshing(true);
        setRefreshError('');
        try {
            await onRefresh();
        } catch {
            setRefreshError('Unable to refresh tract amounts. Please try again.');
        } finally {
            setIsRefreshing(false);
        }
    };

    const openLegalModal = async (index: number) => {
        const tract = tracts[index];
        setLegalModalIndex(index);
        setSelectedTractIndex(index);
        setLegalDescription('');

        if (!tract?.trId) {
            setLegalDescription('No legal description is available for this tract.');
            return;
        }

        try {
            const result = await get(`/v1.0/leases/tracts/${tract.trId}/legal-description`);
            if (response.ok) {
                setLegalDescription(result?.legalDescription || 'No legal description is available for this tract.');
                return;
            }

            setLegalDescription('Unable to load the legal description.');
        } catch {
            setLegalDescription('Unable to load the legal description.');
        }
    };

    const navigateLegalModal = (direction: -1 | 1) => {
        if (legalModalIndex === null) {
            return;
        }

        const nextIndex = legalModalIndex + direction;
        if (nextIndex < 0 || nextIndex >= tracts.length) {
            return;
        }

        openLegalModal(nextIndex);
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <rect x="3" y="3" width="18" height="13" rx="2" />
                        <path d="M3 9h18" />
                        <path d="M9 21V9" />
                    </svg>
                    Tract Information
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {tracts.length > 0 && (
                        <span style={{ fontSize: styles.typography.fontSize.sm, color: styles.colors.gray600 }}>
                            {tracts.length} tract{tracts.length !== 1 ? 's' : ''}
                        </span>
                    )}
                    {onRefresh && (
                        <button type="button" onClick={() => void refreshTracts()} disabled={isRefreshing} style={styles.buttonSecondary}>
                            {isRefreshing ? 'Refreshing…' : 'Refresh'}
                        </button>
                    )}
                    {isEditable && (
                        <button
                            onClick={handleAddTract}
                            style={styles.buttonPrimary}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Add Tract
                        </button>
                    )}
                </div>
            </h2>

            {refreshError && (
                <div role="alert" style={{ marginBottom: '1rem', color: styles.colors.danger }}>
                    {refreshError}
                </div>
            )}

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: bonusPerAcreDiffers ? '#fffbeb' : styles.colors.gray50,
                    border: `1px solid ${bonusPerAcreDiffers ? styles.colors.warning : styles.colors.border}`,
                    borderRadius: '6px',
                }}
            >
                <div style={{ minWidth: '190px' }}>
                    <div style={styles.fieldLabel}>Calculated Bonus / Acre</div>
                    <div
                        data-testid="calculated-bonus-per-acre"
                        style={{
                            marginTop: '0.125rem',
                            color: bonusPerAcreDiffers ? '#92400e' : styles.colors.gray900,
                            fontSize: styles.typography.fontSize.lg,
                            fontWeight: styles.typography.fontWeight.bold,
                        }}
                    >
                        {tractBonusSummary.bonusPerAcre === null ? '-' : formatCurrency(tractBonusSummary.bonusPerAcre)}
                    </div>
                </div>
                <div style={{ color: styles.colors.gray600, fontSize: styles.typography.fontSize.sm }}>
                    Total tract bonus {formatCurrency(tractBonusSummary.totalBonus)} ÷ total net acres {formatNumber(tractBonusSummary.totalNetAcres)}
                </div>
                {bonusPerAcreDiffers && (
                    <div
                        role="status"
                        style={{
                            marginLeft: 'auto',
                            padding: '0.375rem 0.625rem',
                            color: '#92400e',
                            backgroundColor: '#fef3c7',
                            borderRadius: '4px',
                            fontSize: styles.typography.fontSize.sm,
                            fontWeight: styles.typography.fontWeight.semibold,
                        }}
                    >
                        Differs from Master Bonus / Acre ({formatCurrency(leaseMaster.bonusAcre)})
                    </div>
                )}
            </div>

            {tracts.length === 0 ? (
                <div style={styles.emptyState}>
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={styles.colors.gray400}
                        strokeWidth="1.5"
                        style={{ margin: '0 auto 1rem' }}
                    >
                        <rect x="3" y="3" width="18" height="13" rx="2" />
                        <path d="M3 9h18" />
                        <path d="M9 21V9" />
                        <path d="M3 9v7" />
                        <path d="M21 9v7" />
                    </svg>
                    <p style={{ margin: 0, color: styles.colors.gray600, fontSize: styles.typography.fontSize.lg }}>No tract data available</p>
                    {isEditable && (
                        <p style={{ marginTop: '0.5rem', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray500 }}>
                            Click &quot;Add Tract&quot; to create your first tract
                        </p>
                    )}
                </div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.tableHeader}>Client</th>
                                    <th style={styles.tableHeader}>Project</th>
                                    <th style={styles.tableHeader}>Section</th>
                                    <th style={styles.tableHeader}>Tract</th>
                                    <th style={styles.tableHeader}>Horz</th>
                                    <th style={styles.tableHeader}>Map</th>
                                    <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Net Acres</th>
                                    <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Gross Acres</th>
                                    <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Min. Int.</th>
                                    <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Bonus</th>
                                    <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Rental</th>
                                    <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Fees</th>
                                    <th style={styles.tableHeader}>Type</th>
                                    <th style={styles.tableHeader}>Legal</th>
                                    {isEditable && <th style={styles.tableHeader}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {tracts.map((tract, index) => {
                                    const isSelected = selectedTractIndex === index;
                                    const isExpanded = expandedTractIndex === index;

                                    return (
                                        <tr
                                            key={tract.ltTg || `${tract.trId}-${index}`}
                                            style={{
                                                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                                                backgroundColor: isSelected ? '#dbeafe' : isExpanded ? styles.colors.bgSecondary : 'transparent',
                                                boxShadow: isSelected ? `inset 4px 0 0 ${styles.colors.primary}` : undefined,
                                                outline: isSelected ? `1px solid ${styles.colors.primaryLight}` : undefined,
                                                outlineOffset: '-1px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => setSelectedTractIndex(index)}
                                            onMouseEnter={(e) => {
                                                if (!isExpanded && !isSelected) {
                                                    e.currentTarget.style.backgroundColor = styles.colors.gray50;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isExpanded && !isSelected) {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }
                                            }}
                                        >
                                            <td style={styles.tableCell}>{tract.clNo || '-'}</td>
                                            <td style={styles.tableCell}>{tract.prNo || '-'}</td>
                                            <td style={styles.tableCell}>{tract.scNo || '-'}</td>
                                            <td style={styles.tableCell}>
                                                <span style={{ fontWeight: styles.typography.fontWeight.medium }}>{formatTractNumber(tract)}</span>
                                            </td>
                                            <td style={styles.tableCell}>{tract.hzNo || '-'}</td>
                                            <td style={styles.tableCell}>{tract.mapLocation || '-'}</td>
                                            <td style={{ ...styles.tableCell, textAlign: 'right' }}>{formatNumber(tract.net)}</td>
                                            <td style={{ ...styles.tableCell, textAlign: 'right' }}>{formatNumber(tract.gross)}</td>
                                            <td style={{ ...styles.tableCell, textAlign: 'right' }}>{formatNumber(tract.interest)}</td>
                                            <td style={{ ...styles.tableCell, textAlign: 'right', fontWeight: styles.typography.fontWeight.medium }}>
                                                {formatCurrency(tract.bonus)}
                                            </td>
                                            <td style={{ ...styles.tableCell, textAlign: 'right' }}>{formatCurrency(tract.rental)}</td>
                                            <td style={{ ...styles.tableCell, textAlign: 'right' }}>{formatCurrency(tract.fees)}</td>
                                            <td style={styles.tableCell}>{tract.ownershipType || '-'}</td>
                                            <td style={styles.tableCell}>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openLegalModal(index);
                                                    }}
                                                    style={{
                                                        ...styles.buttonSecondary,
                                                        padding: '0.25rem 0.5rem',
                                                        fontSize: styles.typography.fontSize.xs,
                                                    }}
                                                >
                                                    Legal
                                                </button>
                                            </td>
                                            {isEditable && (
                                                <td style={styles.tableCell}>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button
                                                            onClick={() => toggleTractExpansion(index)}
                                                            style={{
                                                                ...styles.buttonSecondary,
                                                                padding: '0.375rem 0.75rem',
                                                                fontSize: styles.typography.fontSize.sm,
                                                            }}
                                                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                                                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                                                        >
                                                            {expandedTractIndex === index ? (
                                                                <>
                                                                    <svg
                                                                        width="14"
                                                                        height="14"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        strokeWidth="2"
                                                                    >
                                                                        <polyline points="18 15 12 9 6 15" />
                                                                    </svg>
                                                                    Collapse
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <svg
                                                                        width="14"
                                                                        height="14"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        strokeWidth="2"
                                                                    >
                                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                    </svg>
                                                                    Edit
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemoveTract(index)}
                                                            style={{
                                                                ...styles.buttonDanger,
                                                                padding: '0.375rem 0.75rem',
                                                                fontSize: styles.typography.fontSize.sm,
                                                            }}
                                                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                                                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                                                        >
                                                            <svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                            >
                                                                <polyline points="3 6 5 6 21 6" />
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                <tr style={{ borderTop: `2px solid ${styles.colors.border}` }}>
                                    <td
                                        colSpan={6}
                                        style={{
                                            ...styles.tableCell,
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                            fontSize: styles.typography.fontSize.base,
                                        }}
                                    >
                                        Totals
                                    </td>
                                    <td
                                        style={{
                                            ...styles.tableCell,
                                            textAlign: 'right',
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                        }}
                                    >
                                        {formatNumber(totals.net)}
                                    </td>
                                    <td
                                        style={{
                                            ...styles.tableCell,
                                            textAlign: 'right',
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                        }}
                                    >
                                        {formatNumber(totals.gross)}
                                    </td>
                                    <td
                                        style={{
                                            ...styles.tableCell,
                                            textAlign: 'right',
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                        }}
                                    >
                                        -
                                    </td>
                                    <td
                                        style={{
                                            ...styles.tableCell,
                                            textAlign: 'right',
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                        }}
                                    >
                                        {formatCurrency(totals.bonus)}
                                    </td>
                                    <td
                                        style={{
                                            ...styles.tableCell,
                                            textAlign: 'right',
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                        }}
                                    >
                                        {formatCurrency(totals.rental)}
                                    </td>
                                    <td
                                        style={{
                                            ...styles.tableCell,
                                            textAlign: 'right',
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                        }}
                                    >
                                        {formatCurrency(totals.fees)}
                                    </td>
                                    <td style={styles.tableCell} />
                                    <td style={styles.tableCell} />
                                    {isEditable && <td style={styles.tableCell} />}
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {selectedTract && (
                        <div
                            style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                backgroundColor: styles.colors.gray50,
                                border: `1px solid ${styles.colors.border}`,
                                borderRadius: '6px',
                                display: 'grid',
                                gridTemplateColumns:
                                    'minmax(140px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(220px, 2fr) minmax(180px, 1.5fr)',
                                gap: '0.75rem',
                            }}
                        >
                            <ReadOnlyDetail label="Client" value={selectedTract.client} />
                            <ReadOnlyDetail label="Prospect" value={selectedTract.prospect} />
                            <ReadOnlyDetail label="Section/Survey" value={selectedTract.section} />
                            <ReadOnlyDetail label="Tract" value={selectedTract.tractDescription} />
                            <ReadOnlyDetail label="Horizon" value={selectedTract.horizonDescription} />
                        </div>
                    )}

                    {isEditable && expandedTractIndex !== null && tracts[expandedTractIndex] && (
                        <div
                            style={{
                                ...styles.cardBase,
                                marginTop: '1.5rem',
                                backgroundColor: styles.colors.bgSecondary,
                                border: `2px solid ${styles.colors.primary}`,
                            }}
                        >
                            <h3
                                style={{
                                    margin: '0 0 1.5rem 0',
                                    fontSize: styles.typography.fontSize.xl,
                                    color: styles.colors.gray900,
                                    fontWeight: styles.typography.fontWeight.semibold,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                }}
                            >
                                Edit Tract {expandedTractIndex + 1}
                            </h3>

                            <div
                                style={{
                                    marginBottom: '2rem',
                                    padding: '1.5rem',
                                    backgroundColor: styles.colors.white,
                                    borderRadius: '8px',
                                    border: `1px solid ${styles.colors.gray200}`,
                                }}
                            >
                                <h4
                                    style={{
                                        margin: '0 0 1rem 0',
                                        fontSize: styles.typography.fontSize.lg,
                                        color: styles.colors.gray800,
                                        fontWeight: styles.typography.fontWeight.medium,
                                    }}
                                >
                                    Identification
                                </h4>
                                <div style={styles.gridCols3}>
                                    <Field
                                        label="Client #"
                                        value={tracts[expandedTractIndex].clNo?.toString() || ''}
                                        type="number"
                                        onChange={(value) => handleNumberFieldChange(expandedTractIndex, 'clNo', value)}
                                    />
                                    <Field
                                        label="Prospect #"
                                        value={tracts[expandedTractIndex].prNo?.toString() || ''}
                                        type="number"
                                        onChange={(value) => handleNumberFieldChange(expandedTractIndex, 'prNo', value)}
                                    />
                                    <Field
                                        label="Section #"
                                        value={tracts[expandedTractIndex].scNo?.toString() || ''}
                                        type="number"
                                        onChange={(value) => handleNumberFieldChange(expandedTractIndex, 'scNo', value)}
                                    />
                                    <Field
                                        label="Tract #"
                                        value={tracts[expandedTractIndex].trNo?.toString() || ''}
                                        type="number"
                                        onChange={(value) => handleNumberFieldChange(expandedTractIndex, 'trNo', value)}
                                    />
                                    <Field
                                        label="Tract Suffix"
                                        value={tracts[expandedTractIndex].trCno || ''}
                                        onChange={(value) => handleTractChange(expandedTractIndex, 'trCno', value)}
                                    />
                                    <Field
                                        label="Horizon #"
                                        value={tracts[expandedTractIndex].hzNo?.toString() || ''}
                                        type="number"
                                        onChange={(value) => handleNumberFieldChange(expandedTractIndex, 'hzNo', value)}
                                    />
                                    <Field
                                        label="Ownership Type"
                                        value={tracts[expandedTractIndex].ownershipType?.toString() || ''}
                                        type="text"
                                        onChange={(value) => handleTractChange(expandedTractIndex, 'ownershipType', value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {legalModalIndex !== null && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(17, 24, 39, 0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '1rem',
                    }}
                    onClick={() => setLegalModalIndex(null)}
                >
                    <div
                        style={{
                            backgroundColor: styles.colors.white,
                            borderRadius: '6px',
                            width: 'min(900px, 95vw)',
                            minHeight: '430px',
                            boxShadow: styles.shadows.xl,
                            border: `1px solid ${styles.colors.gray300}`,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                padding: '0.5rem 0.75rem',
                                borderBottom: `1px solid ${styles.colors.border}`,
                                backgroundColor: styles.colors.gray50,
                                fontWeight: styles.typography.fontWeight.semibold,
                                color: styles.colors.gray900,
                            }}
                        >
                            Legal Description for Tract {formatTractNumber(tracts[legalModalIndex])}
                        </div>
                        <div
                            style={{
                                padding: '0.75rem',
                                fontSize: styles.typography.fontSize.sm,
                                lineHeight: styles.typography.lineHeight.normal,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'normal',
                                wordBreak: 'normal',
                                flex: 1,
                                overflowY: 'auto',
                                color: styles.colors.gray900,
                            }}
                        >
                            {loading ? 'Loading legal description...' : legalDescription}
                        </div>
                        <div
                            style={{
                                padding: '0.75rem',
                                borderTop: `1px solid ${styles.colors.border}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '0.75rem',
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => navigateLegalModal(-1)}
                                disabled={legalModalIndex === 0}
                                style={{
                                    ...styles.buttonSecondary,
                                    opacity: legalModalIndex === 0 ? 0.5 : 1,
                                }}
                            >
                                Previous Tract in Lease
                            </button>
                            <button type="button" onClick={() => setLegalModalIndex(null)} style={styles.buttonSecondary}>
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={() => navigateLegalModal(1)}
                                disabled={legalModalIndex === tracts.length - 1}
                                style={{
                                    ...styles.buttonSecondary,
                                    opacity: legalModalIndex === tracts.length - 1 ? 0.5 : 1,
                                }}
                            >
                                Next Tract in Lease
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ReadOnlyDetail = ({ label, value }: { label: string; value?: string }) => (
    <div style={styles.fieldContainer}>
        <label style={styles.fieldLabel}>{label}</label>
        <div
            style={{
                ...styles.inputDisabled,
                minHeight: '2.25rem',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {value || '-'}
        </div>
    </div>
);

const Field = ({
    label,
    value,
    type = 'text',
    prefix = '',
    onChange,
}: {
    label: string;
    value: string;
    type?: string;
    prefix?: string;
    onChange?: (value: string) => void;
}) => (
    <div style={styles.fieldContainer}>
        <label
            style={{
                ...styles.fieldLabel,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
            }}
        >
            {label}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            {prefix && (
                <span
                    style={{
                        position: 'absolute',
                        left: '1rem',
                        color: styles.colors.gray500,
                        fontSize: styles.typography.fontSize.base,
                        fontWeight: styles.typography.fontWeight.medium,
                    }}
                >
                    {prefix}
                </span>
            )}
            <LeaseRecordInput
                type={type}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                autoComplete="off"
                onFocus={(e) => {
                    e.target.style.borderColor = styles.colors.borderFocus;
                    e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = styles.colors.gray300;
                    e.target.style.boxShadow = 'none';
                }}
                style={{
                    ...styles.inputBase,
                    paddingLeft: prefix ? '2rem' : '1rem',
                }}
            />
        </div>
    </div>
);

export default TractSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Tract.tsx